import React, { useContext, useEffect, useState } from 'react';
import { Redirect, useParams } from 'react-router';
import { useHistory } from 'react-router-dom';

import { ROUTING_CONFIG, SESSION_STATES, USER_ROLES } from '@uplift/constants';
import { CurrentuserContext } from '@uplift/contexts';
import {
  trackJoinSessionClick, trackRejoinSessionClick, useL10n, useUpBreakpoint
} from '@uplift/hooks';
import { useChangeAppointmentState, useTwilioToken } from '@uplift/queries';
import { Loading } from '@uplift/pages';
import { PreVideoCallPage, VideoCallPage, useVideoContext, useRoomState, VideoProvider } from '@uplift/video-call';
import { CalendarDateService } from '@uplift/services';
import { ProviderMedicalRole, UserRole } from '@uplift/types';
import {
  ContextualNavigationBackButton, ContextualNavigationHeaderProps, ContextualNavigationLayout,
  ContextualNavigationLogOut, MainLayoutContainer, ProviderLayout,
  useAlert,
} from '@uplift/ui-v2';
import { formatUserFullName } from '@uplift/utils';

import { PsychiatryReferDrawer } from '../../components';
import { HungUpPage } from '../../pages';
import { PsychiatryNoteLiveForm } from '../../components/PsychiatryNotes';


export const AppointmentSessionPageContent: React.VFC<{ appointmentId: string }> = () => {
  const me = useContext(CurrentuserContext);
  const isSmUp = useUpBreakpoint('sm');
  const { connect: videoConnect, room, localTracks, isConnecting, isLoading, appointment, patient, provider } = useVideoContext();
  const localTracksRef = React.useRef(localTracks);
  const roomState = useRoomState();
  const params = useParams<{ appointmentId: string }>();
  const alert = useAlert();
  const l10n = useL10n();
  const psychiatryAvailable = me.medicalRole === ProviderMedicalRole.therapist;

  const [ isHungUp, setHungUp ] = useState(false);
  // const [ error, showError ] = useState(null);

  const { twilioToken, fetching: twilioTokenFetching } = useTwilioToken({ appointmentId: params?.appointmentId ?? '' });
  const { changeAppointmentState } = useChangeAppointmentState();
  const { push } = useHistory();

  const availableStates = [
    SESSION_STATES.initial,
    SESSION_STATES.started,
    SESSION_STATES.scheduled,
  ];

  ///// Cleanup tracks that may still be on if the user leaves the session route
  useEffect(() => {
    localTracksRef.current = localTracks;
  }, [ localTracks ]);

  useEffect(() => {
    return () => {
      localTracksRef.current.forEach(track => {
        track.stop();
      });
    };
  }, []);
  /////

  const appointmentId = appointment?.id ?? '';

  const completeSessionHandler = async () => {
    const result = await changeAppointmentState(
      appointment.id,
      SESSION_STATES.completed
    );

    if (!result?.error) {
      push(`/appointments/${appointmentId}/submit-session`);
    } else {
      alert.error(result?.error, { title: l10n.general.error });
    }
  };

  const startVideoCall = (closeDialog: boolean) => {
    trackJoinSessionClick({ userType: UserRole.provider });
    videoConnect(twilioToken);

    if (closeDialog) {
      setHungUp(false);
    }
  };

  const rejoinCall = () => {
    trackRejoinSessionClick({ userType: UserRole.provider });
    setHungUp(false);
  };

  const endCall = () => {
    setHungUp(true);
    room?.disconnect();
  };

  const renderPsychiatryReferDrawer = React.useMemo(() => {
    if (psychiatryAvailable && appointment?.patientId) {
      return (onClose: () => void) => (
        <PsychiatryReferDrawer
          onClose={onClose}
          patientId={appointment?.patientId}
          patientName={`${patient.profile.preferredName || patient.user.firstName}`}
          patientState={patient.profile?.address.stateId}
        />
      );
    }

    return undefined;
  }, [ appointment, patient, psychiatryAvailable ]);

  if (isLoading || twilioTokenFetching || isConnecting) {
    return <Loading />;
  }

  if (isHungUp) {
    return (
      <ProviderLayout
        logoLink=""
        background="white"
        navigationVariant="clear"
        showClientsSearch={false}
        showNavigationLinks={false}
      >
        <HungUpPage
          me={me}
          appointment={{
            ...appointment,
            patientId: patient.user.id,
            providerId: provider.user.id,
          }}
          reEnterCall={rejoinCall}
          completeSession={completeSessionHandler}
        />
      </ProviderLayout>
    );
  }

  if (!isConnecting && room && roomState === 'connected') {
    return (
      <VideoCallPage
        me={me}
        appointment={appointment}
        onHungUp={endCall}
        SessionNoteComponent={PsychiatryNoteLiveForm}
        renderPsychiatryDrawer={renderPsychiatryReferDrawer}
      />
    );
  }

  const now = CalendarDateService.date();

  if (
    !appointment
    || now.isAfter(CalendarDateService.date(appointment.toDatetime).add(2, 'hours'))
    || !availableStates.includes(appointment.state)
  ) {
    return <Redirect to={ROUTING_CONFIG.main} />;
  }

  const headerProps: ContextualNavigationHeaderProps = {
    hideLogo: true,
    leftButton: <ContextualNavigationBackButton />,
    rightButton: (
      <ContextualNavigationLogOut name={
        formatUserFullName({
          firstName: me.firstName,
          lastName: me.lastName,
          preferredFirstName: me.preferredName,
          fallback: me.email,
        })}
      />
    )
  };

  const content = (
    <PreVideoCallPage
      me={me}
      companionRole={USER_ROLES.client}
      appointment={appointment}
      onStart={startVideoCall}
      blurAvailable
    />
  );

  return isSmUp ? (
    <ProviderLayout>
      <MainLayoutContainer>
        {content}
      </MainLayoutContainer>
    </ProviderLayout>
  ) : (
    <ContextualNavigationLayout headerProps={headerProps}>
      {content}
    </ContextualNavigationLayout>
  );
};


export const AppointmentSessionPage = () => {
  const params = useParams<{ appointmentId: string }>();

  return (
    <VideoProvider
      appointmentId={params.appointmentId}
      onError={(e) => console.log(e)}
    >
      <AppointmentSessionPageContent
        appointmentId={params.appointmentId}
      />
    </VideoProvider>
  );
};
